import { isPermissionsRecord } from "./typeguards"

import { CHAT_PERMISSION_KEYS, LEGACY_CHAT_SETTING_KEY_ALIASES } from "../model/constants"
import type { ChatPermissions, GroupSettingsResponse, UpdateGroupSettingsRequest } from "../model/types"

const normalizeChatPermissions = (permissions?: Record<string, boolean>): ChatPermissions => {
  const normalizedPermissions = CHAT_PERMISSION_KEYS.reduce<ChatPermissions>((acc, key) => {
    acc[key] = false
    return acc
  }, {} as ChatPermissions)
  const seenKeys = new Set<string>()

  if (!permissions) {
    return normalizedPermissions
  }

  for (const key of CHAT_PERMISSION_KEYS) {
    if (!(key in permissions)) {
      continue
    }

    normalizedPermissions[key] = permissions[key]
    seenKeys.add(key)
  }

  for (const [legacyKey, canonicalKey] of Object.entries(LEGACY_CHAT_SETTING_KEY_ALIASES)) {
    if (seenKeys.has(canonicalKey) || !(legacyKey in permissions)) {
      continue
    }

    normalizedPermissions[canonicalKey] = permissions[legacyKey]
  }

  return normalizedPermissions
}

const sanitizeChatPermissionsPatch = (permissions?: Record<string, boolean>): Partial<ChatPermissions> | undefined => {
  if (!permissions) {
    return undefined
  }

  const sanitizedPermissions: Partial<ChatPermissions> = {}
  const seenKeys = new Set<string>()

  for (const key of CHAT_PERMISSION_KEYS) {
    if (!(key in permissions)) {
      continue
    }

    sanitizedPermissions[key] = permissions[key]
    seenKeys.add(key)
  }

  for (const [legacyKey, canonicalKey] of Object.entries(LEGACY_CHAT_SETTING_KEY_ALIASES)) {
    if (seenKeys.has(canonicalKey) || !(legacyKey in permissions)) {
      continue
    }

    sanitizedPermissions[canonicalKey] = permissions[legacyKey]
  }

  return sanitizedPermissions
}

const resolveReplyToBotMessagesEnabled = (settings: {
  ai_response_mode?: unknown
  reply_to_bot_messages_enabled?: unknown
}): boolean => {
  if (typeof settings.reply_to_bot_messages_enabled === "boolean") {
    return settings.reply_to_bot_messages_enabled
  }
  if (typeof settings.ai_response_mode === "string") {
    return settings.ai_response_mode === "reply_only"
  }
  return true
}

export const normalizeGroupSettings = <T extends GroupSettingsResponse>(response: T): T => {
  const settings = response.settings as {
    ai_response_mode?: unknown
    reply_to_bot_messages_enabled?: unknown
    permissions?: unknown
    default_card_style_id?: unknown
    custom_card_image_url?: unknown
  }

  return {
    ...response,
    settings: {
      ...response.settings,
      reply_to_bot_messages_enabled: resolveReplyToBotMessagesEnabled(settings),
      default_card_style_id: typeof settings.default_card_style_id === "number" ? settings.default_card_style_id : null,
      custom_card_image_url: typeof settings.custom_card_image_url === "string" ? settings.custom_card_image_url : null,
      permissions: normalizeChatPermissions(
        isPermissionsRecord(settings.permissions) ? settings.permissions : undefined
      )
    }
  }
}

export const sanitizeGroupSettingsPatch = (request: UpdateGroupSettingsRequest): UpdateGroupSettingsRequest => {
  if (!request.settings.permissions) {
    return request
  }

  return {
    settings: {
      ...request.settings,
      permissions: sanitizeChatPermissionsPatch(request.settings.permissions)
    }
  }
}
